import {
  isAdHocKitSystemProductId,
  isAdHocKitSystemVariantId,
} from "@/modules/orders/domain/ad-hoc-kit";
import { enrichVariantStockFields } from "./shared-stock";
import { isBundleVariant } from "./bundle";

export type VariantForStockAlert = {
  id: string;
  productId?: string;
  stockQuantity: number;
  inventoryVariantId: string | null;
  unitsPerSale: string;
  isInventoryOnly: boolean;
  isActive: boolean;
  lowStockThreshold: number;
  variantType?: string | null;
};

export function buildPoolStockMap(
  variants: Array<{
    id: string;
    stockQuantity: number;
    inventoryVariantId: string | null;
  }>,
) {
  return new Map(
    variants
      .filter((v) => !v.inventoryVariantId)
      .map((v) => [v.id, { stockQuantity: v.stockQuantity }]),
  );
}

export function variantEffectiveStockQty(
  variant: VariantForStockAlert,
  poolById: Map<string, { stockQuantity: number }>,
): number {
  return enrichVariantStockFields(variant, poolById).saleableStockQuantity;
}

/** ไม่นับ pool ที่มี sale variant ผูกอยู่ — ใช้ threshold ของ sale variant แทน */
export function shouldVariantCountForStockAlert(
  variant: VariantForStockAlert,
  variantsInProduct: VariantForStockAlert[],
): boolean {
  if (!variant.isActive) return false;
  if (isBundleVariant(variant.variantType)) return false;
  // placeholder ຊຸດກຳນົດເອງ — ไม่มีสต็อกจริง ไม่ต้องแจ้งเตือน
  if (isAdHocKitSystemVariantId(variant.id)) return false;
  if (variant.productId && isAdHocKitSystemProductId(variant.productId)) {
    return false;
  }
  if (variant.isInventoryOnly && !variant.inventoryVariantId) {
    const hasLinkedSales = variantsInProduct.some(
      (v) => v.inventoryVariantId === variant.id && v.isActive,
    );
    if (hasLinkedSales) return false;
  }
  return true;
}

export function variantIncomingForAlert(
  variant: VariantForStockAlert,
  incomingByVariantId: Map<string, number>,
): number {
  const sourceId = variant.inventoryVariantId ?? variant.id;
  const raw = incomingByVariantId.get(sourceId) ?? 0;
  if (raw <= 0) return 0;
  if (variant.inventoryVariantId) {
    const unitsPerSale = Math.max(Number(variant.unitsPerSale) || 1, 1);
    return Math.floor(raw / unitsPerSale);
  }
  return raw;
}

/** ພິຈາລະນາສະຕັອກຄົງມື + ຈຳນວນທີ່ສັ່ງແລ້ວຍັງບໍ່ຮັບ */
export function variantStockAlertStatus(
  qty: number,
  threshold: number,
  incomingQty = 0,
): "out" | "low" | "ok" {
  const supply = qty + Math.max(0, incomingQty);
  if (supply <= 0) return "out";
  if (supply <= threshold) return "low";
  return "ok";
}

export function countVariantStockAlerts(
  variants: VariantForStockAlert[],
  incomingByVariantId: Map<string, number> = new Map(),
): {
  lowStockCount: number;
  outOfStockCount: number;
} {
  const poolById = buildPoolStockMap(variants);
  const byProduct = new Map<string, VariantForStockAlert[]>();
  for (const v of variants) {
    const pid = v.productId ?? "";
    const list = byProduct.get(pid) ?? [];
    list.push(v);
    byProduct.set(pid, list);
  }

  let lowStockCount = 0;
  let outOfStockCount = 0;

  for (const v of variants) {
    const productVariants = byProduct.get(v.productId ?? "") ?? [v];
    if (!shouldVariantCountForStockAlert(v, productVariants)) continue;
    const qty = variantEffectiveStockQty(v, poolById);
    const incoming = variantIncomingForAlert(v, incomingByVariantId);
    const status = variantStockAlertStatus(
      qty,
      v.lowStockThreshold,
      incoming,
    );
    if (status === "out") outOfStockCount += 1;
    else if (status === "low") lowStockCount += 1;
  }

  return { lowStockCount, outOfStockCount };
}

export function summarizeProductStockAlerts(
  variants: VariantForStockAlert[],
  incomingByVariantId: Map<string, number> = new Map(),
): {
  isLowStock: boolean;
  lowStockVariantCount: number;
} {
  const poolById = buildPoolStockMap(variants);
  let lowStockVariantCount = 0;

  for (const v of variants) {
    if (!v.isActive) continue;
    if (!shouldVariantCountForStockAlert(v, variants)) continue;
    const qty = variantEffectiveStockQty(v, poolById);
    const incoming = variantIncomingForAlert(v, incomingByVariantId);
    const status = variantStockAlertStatus(
      qty,
      v.lowStockThreshold,
      incoming,
    );
    if (status === "low" || status === "out") lowStockVariantCount += 1;
  }

  return {
    isLowStock: lowStockVariantCount > 0,
    lowStockVariantCount,
  };
}
